// src/context/RedliningContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import redliningApi from '../api/redlining';

const RedliningContext = createContext();

export const useRedlining = () => useContext(RedliningContext);

export const RedliningProvider = ({ children }) => {
  // Document state
  const [currentDocument, setCurrentDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  
  // Annotations state
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [activeToolType, setActiveToolType] = useState('pointer');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // Load annotations for the current document
  useEffect(() => {
    if (currentDocument?.id) {
      loadAnnotations(currentDocument.id);
    }
  }, [currentDocument]);
  
  // Load annotations from API
  const loadAnnotations = async (documentId) => {
    try {
      setLoading(true);
      const response = await redliningApi.getAnnotations(documentId);
      setAnnotations(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading annotations:', err);
      setError('Failed to load annotations');
    } finally {
      setLoading(false);
    }
  };
  
  // Create annotation
  const createAnnotation = async (documentId, annotationData) => {
    try {
      setLoading(true);
      const response = await redliningApi.createAnnotation(documentId, annotationData);
      setAnnotations([...annotations, response.data]);
      
      setNotification({
        open: true,
        message: 'Annotation created successfully',
        severity: 'success'
      });
      
      return response.data;
    } catch (err) {
      console.error('Error creating annotation:', err);
      setError('Failed to create annotation');
      setNotification({
        open: true,
        message: 'Failed to create annotation',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Update annotation
  const updateAnnotation = async (annotationId, updates) => {
    try {
      setLoading(true);
      const response = await redliningApi.updateAnnotation(annotationId, updates);
      
      // Update in state
      setAnnotations(annotations.map(a => a.id === annotationId ? response.data : a));
      
      // Update selected annotation if it's the one being updated
      if (selectedAnnotation?.id === annotationId) {
        setSelectedAnnotation(response.data);
      }
      
      setNotification({
        open: true,
        message: 'Annotation updated successfully',
        severity: 'success'
      });
      
      return response.data;
    } catch (err) {
      console.error('Error updating annotation:', err);
      setError('Failed to update annotation');
      setNotification({
        open: true,
        message: 'Failed to update annotation',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Delete annotation
  const deleteAnnotation = async (annotationId) => {
    try {
      setLoading(true);
      await redliningApi.deleteAnnotation(annotationId);
      
      // Remove from state
      setAnnotations(annotations.filter(a => a.id !== annotationId));
      
      // Clear selected annotation if it's the one being deleted
      if (selectedAnnotation?.id === annotationId) {
        setSelectedAnnotation(null);
      }
      
      setNotification({
        open: true,
        message: 'Annotation deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting annotation:', err);
      setError('Failed to delete annotation');
      setNotification({
        open: true,
        message: 'Failed to delete annotation',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Analyze document with AI
  const analyzeDocument = async (documentId) => {
    try {
      setLoading(true);
      
      setNotification({
        open: true,
        message: 'AI analysis started. This may take a moment...',
        severity: 'info'
      });
      
      const response = await redliningApi.analyzeDocumentWithAI(documentId);
      
      // Add new annotations to state
      if (response.data.annotations && response.data.annotations.length > 0) {
        setAnnotations([...annotations, ...response.data.annotations]);
        
        setNotification({
          open: true,
          message: `AI analysis complete. Found ${response.data.annotations.length} issues.`,
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: 'AI analysis complete. No issues found.',
          severity: 'info'
        });
      }
      
      return response.data;
    } catch (err) {
      console.error('Error analyzing document:', err);
      setError('Failed to analyze document');
      setNotification({
        open: true,
        message: 'Failed to analyze document',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Get AI suggestion for a comment
  const getAISuggestion = async (documentId, context) => {
    try {
      setLoading(true);
      const response = await redliningApi.getAISuggestion(documentId, context);
      return response.data.suggestion;
    } catch (err) {
      console.error('Error getting AI suggestion:', err);
      setError('Failed to get AI suggestion');
      setNotification({
        open: true,
        message: 'Failed to get AI suggestion',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Generate summary of annotations
  const generateSummary = async (documentId) => {
    try {
      setLoading(true);
      const response = await redliningApi.generateCommentSummary(documentId);
      return response.data.summary;
    } catch (err) {
      console.error('Error generating summary:', err);
      setError('Failed to generate summary');
      setNotification({
        open: true,
        message: 'Failed to generate summary',
        severity: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Get annotations for the current page
  const getCurrentPageAnnotations = () => {
    return annotations.filter(a => a.page === currentPage);
  };
  
  // Context value
  const value = {
    // Document state
    currentDocument,
    setCurrentDocument,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    scale,
    setScale,
    
    // Annotations state
    annotations,
    setAnnotations,
    selectedAnnotation,
    setSelectedAnnotation,
    activeToolType,
    setActiveToolType,
    
    // UI state
    loading,
    error,
    notification,
    setNotification,
    
    // Methods
    loadAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    analyzeDocument,
    getAISuggestion,
    generateSummary,
    getCurrentPageAnnotations
  };
  
  return (
    <RedliningContext.Provider value={value}>
      {children}
    </RedliningContext.Provider>
  );
};

export default RedliningProvider;